// Global packages and components
import { clsx } from 'clsx'
import {
  useState,
  useEffect,
  useCallback,
  Children,
  isValidElement,
  cloneElement,
} from 'react'
import { flushSync } from 'react-dom'

// Embla
// import Autoplay from 'embla-carousel-autoplay'
import useEmblaCarousel from 'embla-carousel-react'
import {
  EmblaArrows,
  EmblaDots,
  EmblaThumbs,
} from '~/components/partials/embla/elements'

import type { ReactNode } from 'react'
import type { DotColors } from '~/components/partials/embla/elements'
import type { EmblaOptionsType, EmblaCarouselType } from 'embla-carousel'

const TWEEN_FACTOR = 10

const numberWithinRange = (number: number, min: number, max: number): number =>
  Math.min(Math.max(number, min), max)

type PropType = {
  children: ReactNode
  options?: EmblaOptionsType
  auto?: boolean
  arrows?: boolean
  dots?: boolean
  fade?: boolean
  thumbs?: Array<{ image: { src: string; alt: string }; title?: string }>
  classWrapper?: string
  classContainer?: string
  classDots?: string
  classNames?: {
    wrapperAll?: string
    wrapper?: string
    container?: string
    arrowPrev?: string
    arrowNext?: string
    dots?: string
    dot?: string
    dotColor?: DotColors
    dotActiveColor?: DotColors
    thumbs?: string
    thumb?: string
  }
}

type SlideType = {
  children: ReactNode
  index?: number
  active?: number
  className?: string
  opacity?: number
}

// Slide component
const Slide = ({ children, index, active, className, opacity }: SlideType) => (
  <div
    className={clsx(
      `embla__slide ${className ? className : 'shrink-0 grow-0 basis-full'}`,
      {
        'embla__slide--active': index == active,
      }
    )}
    style={{ opacity: opacity }}
  >
    {children}
  </div>
)

// @TODO: Add fade option
// @TODO: Add ability for autoplay to be stopped until block is in viewport

// Main component
const Carousel = ({
  children,
  options,
  auto = true,
  arrows = false,
  dots = false,
  fade = false,
  thumbs,
  classNames,
}: PropType) => {
  // Hooks and states
  // const autoplayOptions = Autoplay({
  //   delay: 4000,
  //   stopOnMouseEnter: true,
  // }) as AutoplayType

  // const pluginOptions = auto ? [autoplayOptions] : []

  const [emblaRef, emblaApi] = useEmblaCarousel(options)
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [tweenValues, setTweenValues] = useState<number[]>([])
  const totalSlides = Children.count(children)

  // Methods
  const onButtonClick = useCallback((emblaApi: EmblaCarouselType) => {
    const { autoplay } = emblaApi.plugins()
    if (!autoplay) return
    // if (autoplay.options.stopOnInteraction !== false) autoplay.stop()
  }, [])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
    emblaThumbsApi && emblaThumbsApi.scrollTo(emblaApi.selectedScrollSnap())
  }, [emblaApi, emblaThumbsApi, setSelectedIndex])

  const onScroll = useCallback(() => {
    if (!emblaApi) return

    const engine = emblaApi.internalEngine()
    const scrollProgress = emblaApi.scrollProgress()

    const styles = emblaApi.scrollSnapList().map((scrollSnap, index) => {
      let diffToTarget = scrollSnap - scrollProgress

      if (engine.options.loop) {
        engine.slideLooper.loopPoints.forEach(loopItem => {
          const target = loopItem.target()
          if (index === loopItem.index && target !== 0) {
            const sign = Math.sign(target)
            if (sign === -1) diffToTarget = scrollSnap - (1 + scrollProgress)
            if (sign === 1) diffToTarget = scrollSnap + (1 - scrollProgress)
          }
        })
      }
      const tweenValue = 1 - Math.abs(diffToTarget * TWEEN_FACTOR)
      return numberWithinRange(tweenValue, 0, 1)
    })
    setTweenValues(styles)
  }, [emblaApi, setTweenValues])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    onScroll()
    emblaApi.on('scroll', () => {
      flushSync(() => onScroll())
    })
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)
    emblaApi.on('reInit', onScroll)
  }, [emblaApi, onSelect])

  return (
    <div className={classNames?.wrapperAll ?? 'relative w-full'}>
      <div
        className={clsx(
          `embla ${classNames?.wrapper ?? 'relative overflow-hidden'}`
        )}
      >
        <div
          className="embla__viewport h-full"
          ref={emblaRef}
        >
          <div
            className={clsx(
              `embla__container flex ${
                classNames?.container ?? 'h-full w-full flex-row'
              }`
            )}
          >
            {Children.map(children, (child, i) => {
              if (isValidElement(child)) {
                const updatedProps = {
                  ...child.props,
                  active: selectedIndex,
                  opacity: fade && tweenValues.length ? tweenValues[i] : 1,
                  index: i,
                }
                return cloneElement(child, updatedProps)
              }

              return child
            })}
          </div>
        </div>
        {arrows && totalSlides > 1 && (
          <EmblaArrows
            emblaApi={emblaApi}
            onButtonClick={onButtonClick}
            classNames={{
              prev: classNames?.arrowPrev,
              next: classNames?.arrowNext,
            }}
          />
        )}
      </div>
      {dots && totalSlides > 1 && (
        <EmblaDots
          emblaApi={emblaApi}
          onButtonClick={onButtonClick}
          classNames={{
            container: classNames?.dots,
            dot: classNames?.dot,
            dotColor: classNames?.dotColor ?? 'white',
            dotActiveColor: classNames?.dotActiveColor ?? 'white',
          }}
        />
      )}
      {thumbs && totalSlides > 1 && (
        <div className="relative">
          <div className="container mx-auto mt-6">
            <div
              className="overflow-hidden"
              ref={emblaThumbsRef}
            >
              <div className="-mx-3 flex flex-row lg:-mx-6">
                <EmblaThumbs
                  emblaMainApi={emblaApi}
                  emblaThumbsApi={emblaThumbsApi}
                  onButtonClick={onButtonClick}
                  thumbs={thumbs}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

Carousel.Slide = Slide

export default Carousel
